import type {StatusTone} from '../components/StatusChip';

/**
 * Plain words for every governed code the forward-flow screens show. A code that is not
 * listed falls back to sentence case, so a new server value still reads as words.
 */
const sentence = (code: string) => code.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

type Word = {label: string; tone: StatusTone};
const w = (label: string, tone: StatusTone): Word => ({label, tone});

const AGREEMENT: Record<string, Word> = {
  DRAFT: w('Terms being entered', 'progress'), IN_REVIEW: w('Waiting for a checker', 'warning'),
  VERIFIED: w('Verified · ready to activate', 'progress'), SCHEDULED: w('Scheduled', 'progress'),
  ACTIVE: w('In force', 'done'), SUPERSEDED: w('Replaced', 'neutral'), WITHDRAWN: w('Withdrawn', 'neutral'),
};
const PROGRAM: Record<string, Word> = {
  DRAFT: w('Being set up', 'progress'), ACTIVE: w('Active', 'done'), SUSPENDED: w('Suspended', 'warning'),
  EXPIRED: w('Expired · in runoff', 'neutral'), TERMINATED: w('Terminated · in runoff', 'neutral'), CLOSED: w('Closed', 'neutral'),
};
const SOURCE: Record<string, Word> = {
  VALIDATED: w('Received', 'done'), INVALID: w('Has errors', 'critical'), SUPERSEDED: w('Replaced', 'neutral'),
  HELD: w('Held', 'warning'), RECEIVED: w('Received', 'done'), READY_FOR_REVIEW: w('Passed evaluation', 'done'),
  BLOCKED: w('Did not pass', 'critical'),
};
const EVALUATION: Record<string, Word> = {
  PASS: w('Passes', 'done'), FAIL: w('Does not pass', 'critical'), INDETERMINATE: w('Needs more evidence', 'warning'),
  PROPOSED: w('To purchase', 'done'), REJECTED: w('Not eligible', 'critical'), HELD: w('Held', 'warning'),
  EXCLUDED: w('Left out', 'neutral'), NOT_APPLICABLE: w('Not applicable', 'neutral'),
};
const LOAN: Record<string, Word> = {
  RESERVED: w('Transfer to confirm', 'progress'), DRAFT: w('Trade created', 'progress'),
  PRECLEAR_PENDING: w('Waiting for pre-clearance', 'warning'), PENDING_PRECLEAR: w('Waiting for pre-clearance', 'warning'),
  PRECLEAR_APPROVED: w('Cleared to book', 'progress'), APPROVED: w('Cleared to book', 'progress'),
  SENT: w('Sent', 'progress'), EXECUTED: w('Executed', 'progress'), POSTED: w('On the official book', 'done'),
  FAILED: w('Refused', 'critical'), REJECTED: w('Refused', 'critical'), CANCELLED: w('Cancelled', 'neutral'),
  PRECLEAR_REJECTED: w('Refused at pre-clearance', 'critical'), VOIDED: w('Voided', 'neutral'), REVERSED: w('Reversed', 'neutral'),
  SETTLED: w('On the official book', 'done'),
};
const PAYMENT: Record<string, Word> = {
  PENDING: w('To be released', 'progress'), PARTIALLY_PREPAID: w('Partly prepaid', 'progress'),
  PREPAID: w('Prepaid', 'done'), RELEASE_RECORDED: w('Released · bank to confirm', 'warning'),
  PARTIALLY_CONFIRMED: w('Partly confirmed', 'warning'), CONFIRMED: w('Confirmed by the bank', 'done'),
  RETURNED: w('Returned by the bank', 'critical'),
};
const DECISION: Record<string, Word> = {
  PENDING: w('Waiting for approval', 'warning'), APPROVED: w('Approved', 'done'), CANCELLED: w('Cancelled', 'neutral'),
};
const AUTHORITY: Record<string, Word> = {
  DRAFT: w('Waiting for a checker', 'warning'), VERIFIED: w('Verified', 'done'), VOID: w('Voided', 'neutral'),
  APPLIED: w('Applied', 'done'),
};

const BOOKS = {agreement: AGREEMENT, program: PROGRAM, source: SOURCE, evaluation: EVALUATION, loan: LOAN,
  payment: PAYMENT, decision: DECISION, authority: AUTHORITY};
export type WordBook = keyof typeof BOOKS;

export const word = (book: WordBook, code?: string | null): Word =>
  !code ? w('—', 'neutral') : BOOKS[book][code] ?? w(sentence(code), 'neutral');

/** What happened, for the activity log. */
const ACTIVITY: Record<string, string> = {
  PROGRAM_CREATED: 'Program created', AGREEMENT_VERSION_CREATED: 'Agreement version started',
  AGREEMENT_DOCUMENT_UPLOADED: 'Agreement PDF stored', TERMS_REVIEWED: 'Agreement terms saved',
  AGREEMENT_IN_REVIEW: 'Agreement sent to a checker', AGREEMENT_VERIFIED: 'Agreement terms verified',
  AGREEMENT_ACTIVE: 'Agreement put in force', AGREEMENT_SCHEDULED: 'Agreement scheduled',
  AGREEMENT_SUPERSEDED: 'Agreement replaced by a newer version', WORK_ASSIGNED: 'Work assigned',
  DELIVERY_RECEIVED: 'Seller offer received', MAPPING_CREATED: 'Column mapping saved',
  DELIVERY_EVALUATED: 'Offer evaluated', CAPACITY_BASELINE_PREPARED: 'Opening capacity recorded',
  CAPACITY_BASELINE_VERIFIED: 'Opening capacity verified', CLAIM_IDENTIFIED: 'Seller claim recorded',
  CLAIM_REVIEWED: 'Seller claim reviewed', CLAIM_NOTICE_RECORDED: 'Claim notice recorded',
  REMEDIATION_PREPARED: 'External purchase drafted', REMEDIATION_RECORDED: 'External purchase recorded',
  REPORT_RENDERED: 'Report produced', REPORT_RESTATED: 'Report restated', REPORT_DELIVERY_RECORDED: 'Report delivery recorded',
  REPORT_DELIVERY_ACKNOWLEDGED: 'Report receipt acknowledged', REPURCHASE_PRICED: 'Repurchase priced',
  REPURCHASE_AGREED: 'Repurchase price agreed', REPURCHASE_STAGED: 'Repurchase trade created',
  REPURCHASE_EXECUTED: 'Loan repurchased by the seller', REPURCHASE_SETTLED: 'Repurchase cash received',
  SERVICER_QUESTION_DRAFTED: 'Question to the servicer drafted', SERVICER_REMITTANCE_RECORDED: 'Servicer remittance recorded',
  SERVICER_REMITTANCE_CONFIRMED: 'Servicer remittance confirmed', SERVICING_PACKAGE_RECEIVED: 'Servicer package received',
  SERVICING_PACKAGE_RELEASED: 'Servicer package released', SERVICING_ITEM_DISPOSITIONED: 'Difference explained',
  SERVICING_BOOKING_APPROVED: 'Servicing booking approved', SERVICING_BOOKED: 'Servicing booked',
  SERVICING_BOOKED_PARTIAL: 'Servicing partly booked', SERVICING_BOOKING_RETRIED: 'Servicing booking retried',
  SERVICING_CLOSE_ATTESTED: 'Servicing close attested', SERVICING_CLOSE_SIGNED_OFF: 'Servicing period signed off',
  STOP_EVENT_DECLARED: 'Stop declared', STOP_EVENT_ACTIVE: 'Stop put in force', STOP_RESUME_PREPARED: 'Resume prepared',
  STOP_EVENT_RESUMED: 'Purchasing resumed', VALUATION_CALCULATED: 'Valuation calculated',
  VALUATION_REVIEWED: 'Valuation reviewed', VALUATION_APPROVED: 'Valuation approved · marks posted',
  PURCHASE_DECISION_PREPARED: 'Purchase sent for approval', PURCHASE_DECISION_APPROVED: 'Purchase approved',
  PURCHASE_DECISION_CANCELLED: 'Purchase request withdrawn', PURCHASE_TRANSFER_CONFIRMED: 'Loan transfer confirmed',
  PURCHASE_CHILD_POSTED: 'Loan booked to the official record', PURCHASE_PREPAYMENT_CONFIRMED: 'Prepayment confirmed',
  PURCHASE_PAYMENT_RELEASE_RECORDED: 'Payment release recorded', PURCHASE_BANK_CONFIRMED: 'Bank payment confirmed',
  PURCHASE_BANK_RETURNED: 'Bank payment returned', PURCHASE_HOLDBACK_RELEASED: 'Holdback released',
  PROGRAM_STATE_CHANGE_PREPARED: 'Program change drafted', PROGRAM_SUSPENDED: 'Program suspended',
  PROGRAM_ACTIVE: 'Program resumed', PROGRAM_EXPIRED: 'Program expired · runoff began',
  PROGRAM_TERMINATED: 'Program terminated · runoff began',
};
export const activityWords = (code?: string | null): string => (code ? ACTIVITY[code] ?? sentence(code) : '—');

/** Agreement terms: the label, and the words for each governed option. */
export const TERM_LABELS: Record<string, string> = {
  sellerName: 'Seller legal name', originatorNamespace: 'Originator loan-id prefix', servicerName: 'Servicer legal name',
  legalAssessmentRef: 'Legal assessment reference', settlementInstructionsRef: 'Settlement instruction reference',
  effectiveFrom: 'Effective from', pricePer100: 'Price per $100 of balance', cumulativeLimitUpb: 'Total purchase limit',
  monthlyLimitUpb: 'Monthly purchase limit', purchaseWeekday: 'Purchase day', selectionMode: 'Buyer may select',
  buyerObligation: 'Buyer obligation', sellerObligation: 'Seller obligation',
  underwritingPolicyVersion: 'Underwriting policy version', dayCountConvention: 'Interest day count',
  minCreditScore: 'Minimum credit score', maxStateConcentrationPct: 'Maximum share in one state',
  servicingDueDay: 'Servicer report due', holdbackDueDays: 'Holdback due',
  buyerFeeTreatment: 'Buyer fee accounting', priceAdjustmentTreatment: 'Price adjustment accounting',
  allowBuyerFee: 'Buyer fee', allowPriceAdjustment: 'Price adjustment', allowHoldback: 'Holdback',
  allowPriorCredit: 'Credit for an earlier payment', internalProfileReviewed: 'Operating assumptions reviewed',
  currency: 'Currency', assetType: 'Asset type', rateType: 'Rate type', paymentFrequency: 'Payment frequency',
};
export const termLabel = (key: string): string => TERM_LABELS[key] ?? sentence(key.replace(/([A-Z])/g, '_$1'));

export const TERM_OPTIONS: Record<string, Array<[string, string]>> = {
  purchaseWeekday: [['MONDAY', 'Monday'], ['TUESDAY', 'Tuesday'], ['WEDNESDAY', 'Wednesday'], ['THURSDAY', 'Thursday'], ['FRIDAY', 'Friday']],
  selectionMode: [['PARTIAL', 'Any loans from the offer'], ['TAKE_ALL', 'The whole offer or none']],
  buyerObligation: [['DISCRETIONARY', 'May decline an offer'], ['COMMITTED', 'Must buy eligible loans']],
  sellerObligation: [['BEST_EFFORTS', 'Best efforts to offer'], ['COMMITTED', 'Must offer eligible loans']],
  dayCountConvention: [['ACT/365F', 'Actual/365 fixed'], ['ACT/360', 'Actual/360']],
  buyerFeeTreatment: [['CAPITALIZE', 'Added to the cost of the loans']],
  priceAdjustmentTreatment: [['PRINCIPAL_COST', 'Adjusts the cost of the loans']],
};
export const optionWords = (key: string, value: unknown): string => {
  const found = TERM_OPTIONS[key]?.find(([code]) => code === value);
  return found ? found[1] : sentence(String(value));
};

/** The loan fields a seller file is mapped to. */
export const LOAN_FIELD_LABELS: Record<string, string> = {
  sourceLoanId: "Seller's loan id", obligorToken: 'Borrower reference', originationDate: 'Origination date',
  maturityDate: 'Maturity date', nextDueDate: 'Next payment due', originalPrincipal: 'Original principal',
  cutoffUpb: 'Balance at cutoff', couponRatePct: 'Interest rate %', creditScore: 'Credit score',
  stateCode: 'State', pricePer100: 'Price per $100', purchasedInterest: 'Purchased interest',
  currency: 'Currency', productSubtype: 'Product type', rateType: 'Rate type', paymentFrequency: 'Payment frequency',
};
export const loanFieldLabel = (key: string): string => LOAN_FIELD_LABELS[key] ?? sentence(key.replace(/([A-Z])/g, '_$1'));

/** Evaluation checks, as a reviewer would name them. */
const RULES: Record<string, string> = {
  MIN_CREDIT_SCORE: 'Credit score', MAX_STATE_CONCENTRATION: 'Share in one state', FIXED_PRICE: 'Price',
  IDENTITY: 'Loan identity', CURRENCY: 'Currency', COUPON_RATE: 'Interest rate', LOAN_DATE_ORDER: 'Order of the loan dates',
  ORIGINAL_PRINCIPAL: 'Original principal', POSITIVE_UPB: 'Balance above zero', PURCHASED_INTEREST: 'Purchased interest',
  STATE_REQUIRED: 'State', UPB_NOT_ABOVE_ORIGINAL: 'Balance within original principal',
  BUYER_SELECTION: 'Left out by the reviewer', MATURITYDATE: 'Maturity date', NEXTDUEDATE: 'Next payment due',
  ORIGINATIONDATE: 'Origination date',
};
const DIAGNOSTICS: Record<string, string> = {
  ALREADY_OWNED_IN_IBOR: 'Already owned on the official book', DUPLICATE_CANONICAL_ASSET: 'Same loan offered twice',
  DUPLICATE_IN_DELIVERY: 'Loan id appears twice in this file', INVALID_STATE_CODE: 'State code is not valid',
  MISSING_CREDIT_SCORE: 'Credit score is missing', MISSING_CURRENCY: 'Currency is missing',
  MISSING_OBLIGOR_TOKEN: 'Borrower reference is missing', MISSING_PRODUCT_SUBTYPE: 'Product type is missing',
  MISSING_SOURCE_LOAN_ID: 'Loan id is missing', NO_ACTIVE_AGREEMENT: 'No agreement was in force',
  OVERLAPPING_OPEN_BATCH: 'Loan is in another open offer', STATE_CONFLICT_WITH_KNOWN_IDENTITY: 'State differs from the loan we know',
  UNRESOLVED_ALIAS: 'Loan id is not recognized', UNSUPPORTED_PAYMENT_FREQUENCY: 'Payment frequency is not supported',
  UNSUPPORTED_RATE_TYPE: 'Rate type is not supported',
};
export const diagnosticWords = (code: string): string => DIAGNOSTICS[code] ?? sentence(code);
/** A failed source check arrives as SOURCE_<diagnostic>. */
export const ruleWords = (code: string): string =>
  RULES[code] ?? (code.startsWith('SOURCE_') ? diagnosticWords(code.slice(7)) : sentence(code));

export const money = (value: unknown, digits = 2): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US', {style: 'currency', currency: 'USD',
    minimumFractionDigits: digits, maximumFractionDigits: digits}) : '—';
};
export const day = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-US', {day: 'numeric', month: 'short', year: 'numeric'});
};
export const moment = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('en-US', {day: 'numeric', month: 'short',
    year: 'numeric', hour: 'numeric', minute: '2-digit'});
};
export const count = (n: number | null | undefined, one: string, many = `${one}s`): string =>
  `${(n ?? 0).toLocaleString('en-US')} ${(n ?? 0) === 1 ? one : many}`;
